import React, { useState, useEffect } from "react";
import { ArrowLeft, Trash2 } from "lucide-react";

// Import utility functions from components.js
import { getParliamentaryTerms, getAllPetitions, AUTHORIZED_USER_ID, formatTermDisplay, getPetitionsByTerm, updatePetition, updatePetitionStatusAdmin, createParliamentaryTerm, deleteParliamentaryTerm, updateParliamentaryTerm, getBanStats, getBannedUsers, checkUserBan, refreshBanCache, getMaintenanceMode, setMaintenanceMode, toggleMaintenanceMode } from "./components";


export const TermManagement = ({ user, onBack }) => {
  const [terms, setTerms] = useState([]);
  const [allPetitions, setAllPetitions] = useState([]);
  const [termStats, setTermStats] = useState({}); // Store petition counts for each term
  const [loading, setLoading] = useState(true);
  const [selectedPetitions, setSelectedPetitions] = useState([]);
  const [showCreateTerm, setShowCreateTerm] = useState(false);
  const [showEditTerm, setShowEditTerm] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [showAssignTerm, setShowAssignTerm] = useState(false);
  const [selectedTermForAssignment, setSelectedTermForAssignment] = useState('');
  
  // Ban Management States
  const [banStats, setBanStats] = useState({
    total_banned_users: 0,
    service_available: false,
    cache_info: null
  });
  const [checkUserId, setCheckUserId] = useState('');
  const [banCheckResult, setBanCheckResult] = useState(null);
  const [showBanList, setShowBanList] = useState(false);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);

  // Petition Status Management States
  const [showStatusPanel, setShowStatusPanel] = useState(false);
  const [selectedPetitionId, setSelectedPetitionId] = useState('');
  const [newPetitionStatus, setNewPetitionStatus] = useState('');
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  // New term form data
  const [newTerm, setNewTerm] = useState({
    name: '',
    startDate: '',
    endDate: ''
  });

  // Ban Management Functions
  const loadBanStats = async () => {
    setLoading(true);
    try {
      const stats = await getBanStats();
      setBanStats(stats);
    } catch (error) {
      console.error('Error loading ban stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshBanCacheHandler = async () => {
    setLoading(true);
    try {
      await refreshBanCache();
      await loadBanStats();
    } catch (error) {
      console.error('Error refreshing ban cache:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkUserBanHandler = async () => {
    setLoading(true);
    try {
      const result = await checkUserBan(checkUserId);
      setBanCheckResult(result);
    } catch (error) {
      console.error('Error checking user ban status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBannedUsersList = async () => {
    setLoading(true);
    try {
      const result = await getBannedUsers();
      setBannedUsers(result.banned_users || []);
    } catch (error) {
      console.error('Error loading banned users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load terms and petitions on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [fetchedTerms, petitions] = await Promise.all([
          getParliamentaryTerms(),
          getAllPetitions()
        ]);
        
        setTerms(fetchedTerms);
        setAllPetitions(petitions);
        
        // Calculate petition counts and signature totals for each term
        const stats = {};
        fetchedTerms.forEach(term => {
          const termPetitions = petitions.filter(p => String(p.termId) === String(term.id));
          const totalSignatures = termPetitions.reduce((sum, petition) => sum + (petition.signatures || 0), 0);
          stats[term.id] = {
            petitionCount: termPetitions.length,
            totalSignatures: totalSignatures
          };
        });
        setTermStats(stats);
      } catch (error) {
        console.error('Error loading data:', error);
        setTerms([]);
        setAllPetitions([]);
        setTermStats({});
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
    
    // Load ban stats on component mount
    loadBanStats();
  }, []);

  // Refresh data when terms are updated
  useEffect(() => {
    const handleTermsUpdate = async () => {
      const updatedTerms = await getParliamentaryTerms();
      setTerms(updatedTerms);
      try {
        const petitions = await getAllPetitions();
        setAllPetitions(petitions);
        
        // Update petition counts and signature totals for each term
        const stats = {};
        updatedTerms.forEach(term => {
          const termPetitions = petitions.filter(p => p.termId === term.id);
          const totalSignatures = termPetitions.reduce((sum, petition) => sum + (petition.signatures || 0), 0);
          stats[term.id] = {
            petitionCount: termPetitions.length,
            totalSignatures: totalSignatures
          };
        });
        setTermStats(stats);
      } catch (error) {
        console.error('Error loading petitions:', error);
        setAllPetitions([]);
        setTermStats({});
      }
    };
    
    window.addEventListener('termsUpdated', handleTermsUpdate);
    window.addEventListener('petitionsUpdated', handleTermsUpdate);
    
    return () => {
      window.removeEventListener('termsUpdated', handleTermsUpdate);
      window.removeEventListener('petitionsUpdated', handleTermsUpdate);
    };
  }, []);

  // Track maintenance mode changes
  useEffect(() => {
    // Initial check
    setIsMaintenanceMode(getMaintenanceMode());
    
    // Listen for maintenance mode changes
    const handleMaintenanceChange = () => {
      setIsMaintenanceMode(getMaintenanceMode());
    };

    window.addEventListener('maintenanceModeChanged', handleMaintenanceChange);

    return () => {
      window.removeEventListener('maintenanceModeChanged', handleMaintenanceChange);
    };
  }, []);

  // Petition Status Management Function
  const handleUpdatePetitionStatus = async () => {
    if (!selectedPetitionId || !newPetitionStatus) {
      alert('Please select a petition and status');
      return;
    }

    setStatusUpdateLoading(true);
    try {
      const result = await updatePetitionStatusAdmin(selectedPetitionId, newPetitionStatus);
      
      if (result.success) {
        alert('Petition status updated successfully!');
        
        // Update the local state immediately
        setAllPetitions(prevPetitions => 
          prevPetitions.map(petition => 
            petition.id === selectedPetitionId 
              ? { ...petition, status: newPetitionStatus }
              : petition
          )
        );
        
        // Reset form
        setSelectedPetitionId('');
        setNewPetitionStatus('');
        setShowStatusPanel(false);
        
        // Trigger global petition update event
        window.dispatchEvent(new CustomEvent('petitionsUpdated'));
        
        // Also reload data to ensure consistency
        setTimeout(async () => {
          try {
            const fetchedTerms = await getParliamentaryTerms();
            setTerms(fetchedTerms);
            const petitions = await getAllPetitions();
            setAllPetitions(petitions);
            
            // Calculate petition counts and signature totals for each term
            const stats = {};
            fetchedTerms.forEach(term => {
              const termPetitions = petitions.filter(p => String(p.termId) === String(term.id));
              const totalSignatures = termPetitions.reduce((sum, petition) => sum + (petition.signatures || 0), 0);
              stats[term.id] = {
                petitionCount: termPetitions.length,
                totalSignatures: totalSignatures
              };
            });
            setTermStats(stats);
          } catch (error) {
            console.error('Error loading data:', error);
          }
        }, 500); // Small delay to ensure backend has processed the change
      } else {
        alert(`Failed to update status: ${result.message}`);
      }
    } catch (error) {
      console.error('Error updating petition status:', error);
      alert('Failed to update petition status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleCreateTerm = async () => {
    if (!newTerm.name || !newTerm.startDate || !newTerm.endDate) {
      alert('Please fill in all fields');
      return;
    }

    const newTermData = {
      name: newTerm.name,
      startDate: newTerm.startDate,
      endDate: newTerm.endDate,
      status: 'upcoming'
    };

    try {
      const result = await createParliamentaryTerm(newTermData);
      
      if (result.success) {
        // Refresh terms list
        const updatedTerms = await getParliamentaryTerms();
        setTerms(updatedTerms);
        
        setNewTerm({ name: '', startDate: '', endDate: '' });
        setShowCreateTerm(false);
        
        alert('Parliamentary term created successfully!');
      } else {
        alert(result.message || 'Failed to create parliamentary term');
      }
    } catch (error) {
      console.error('Error creating term:', error);
      alert('Failed to create parliamentary term. Please try again.');
    }
  };

  const handleEditTerm = async () => {
    if (!editingTerm.name || !editingTerm.startDate || !editingTerm.endDate) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const updateData = {
        name: editingTerm.name,
        startDate: editingTerm.startDate,
        endDate: editingTerm.endDate,
        status: editingTerm.status
      };
      
      const result = await updateParliamentaryTerm(editingTerm.id, updateData);
      
      if (result.success) {
        // Refresh terms list
        const updatedTerms = await getParliamentaryTerms();
        setTerms(updatedTerms);
        
        setEditingTerm(null);
        setShowEditTerm(false);
        
        alert('Parliamentary term updated successfully!');
      } else {
        alert(result.message || 'Failed to update parliamentary term');
      }
    } catch (error) {
      console.error('Error updating term:', error);
      alert('Failed to update parliamentary term. Please try again.');
    }
  };

  const handleAssignPetitionsToTerm = async () => {
    if (!selectedTermForAssignment || selectedPetitions.length === 0) {
      alert('Please select a term and at least one petition');
      return;
    }

    try {
      // Update each selected petition via API
      const updatePromises = selectedPetitions.map(petitionId => 
        updatePetition(petitionId, { termId: selectedTermForAssignment })
      );
      
      const results = await Promise.all(updatePromises);
      
      // Check if all updates were successful
      const failedUpdates = results.filter(result => !result.success);
      if (failedUpdates.length > 0) {
        alert(`Failed to update ${failedUpdates.length} petition(s). Please try again.`);
        return;
      }
      
      // Refresh the petition list
      const refreshedPetitions = await getAllPetitions();
      setAllPetitions(refreshedPetitions);
      
      // Reset selection
      setSelectedPetitions([]);
      setSelectedTermForAssignment('');
      setShowAssignTerm(false);
      
      alert(`Successfully assigned ${selectedPetitions.length} petition(s) to the selected term.`);
      
    } catch (error) {
      console.error('Error assigning petitions to term:', error);
      alert('Failed to assign petitions to term. Please try again.');
    }
  };

  const togglePetitionSelection = (petitionId) => {
    if (selectedPetitions.includes(petitionId)) {
      setSelectedPetitions(selectedPetitions.filter(id => id !== petitionId));
    } else {
      setSelectedPetitions([...selectedPetitions, petitionId]);
    }
  };

  const handleDeleteAllPetitions = () => {
    if (window.confirm('⚠️ WARNING: This will permanently delete ALL petitions in the system. This action cannot be undone. Are you absolutely sure?')) {
      if (window.confirm('Are you REALLY sure? This will delete all user-created petitions permanently!')) {
        // Clear all user-created petitions from localStorage
        localStorage.removeItem('user_petitions');
        
        // Reset state
        setAllPetitions(getAllPetitions()); // This will now only show mock petitions
        setSelectedPetitions([]);
        
        // Trigger global update
        window.dispatchEvent(new CustomEvent('petitionsUpdated'));
        
        alert('All petitions have been deleted successfully.');
      }
    }
  };

  const handleMaintenanceMode = () => {
    const isCurrentlyMaintenance = getMaintenanceMode();
    
    if (isCurrentlyMaintenance) {
      // Disable maintenance mode
      if (window.confirm('Are you sure you want to disable maintenance mode? This will allow all users to access the site again.')) {
        setMaintenanceMode(false);
        alert('Maintenance mode disabled. All users can now access the site.');
      }
    } else {
      // Enable maintenance mode
      if (window.confirm('⚠️ WARNING: This will put the site in maintenance mode. Only system administrators will be able to access the site. Are you sure?')) {
        setMaintenanceMode(true);
        alert('Maintenance mode enabled. Only system administrators can now access the site.');
      }
    }
  };

  // Check if user is System Administrator only
  const isSystemAdmin = (user) => {
    if (!user) return false;
    return user.id === AUTHORIZED_USER_ID || user.id === AUTHORIZED_USER_ID.toString() || user.id === '1065731316';
  };

  if (!isSystemAdmin(user)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">Only System Administrators can access term management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading term management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <button
          onClick={onBack}
          className="text-blue-600 hover:text-blue-800 mb-4"
        >
          ← Back to petitions
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Parliamentary Term Management</h1>
        <p className="text-gray-600 mt-2">Manage parliamentary terms and assign petitions to appropriate terms</p>
      </div>

      {/* Term Management Section */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Parliamentary Terms</h2>
          <button
            onClick={() => setShowCreateTerm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Create New Term
          </button>
        </div>

        <div className="space-y-4">
          {terms.map(term => (
            <div key={term.id} className="border rounded-lg p-4 flex justify-between items-center">
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{formatTermDisplay(term)}</h3>
                <div className="flex items-center space-x-4 mt-1">
                  <span className={`text-sm px-2 py-1 rounded capitalize ${
                    term.status === 'current' ? 'bg-green-100 text-green-800' :
                    term.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {term.status}
                  </span>
                  <button
                    onClick={() => {
                      const petitions = getPetitionsByTerm(term.id);
                      if (petitions.length > 0) {
                        alert(`This term has ${petitions.length} petition(s). You can view them below.`);
                        // Scroll to petitions section
                        document.querySelector('[data-term-petitions]')?.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        alert('No petitions found for this term.');
                      }
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm underline"
                  >
                    View Petitions ({(termStats[term.id]?.petitionCount || 0)})
                  </button>
                  <div className="text-sm text-gray-600 mt-1">
                    Signatures: {(termStats[term.id]?.totalSignatures || 0).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setEditingTerm({ ...term });
                    setShowEditTerm(true);
                  }}
                  className="bg-orange-600 text-white px-3 py-1 rounded hover:bg-orange-700 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={async () => {
                    console.log('Delete button clicked for term:', term.id);
                    const petitionCount = termStats[term.id]?.petitionCount || 0;
                    let confirmMessage = 'Are you sure you want to delete this parliamentary term?';
                    
                    if (petitionCount > 0) {
                      confirmMessage = `This term has ${petitionCount} petition(s). Deleting this term will unassign these petitions. Are you sure?`;
                    }
                    
                    console.log('Showing confirm dialog:', confirmMessage);
                    if (window.confirm(confirmMessage)) {
                      try {
                        console.log('User confirmed, calling deleteParliamentaryTerm with ID:', term.id);
                        const result = await deleteParliamentaryTerm(term.id);
                        console.log('Delete result:', result);
                        
                        if (result.success) {
                          // Refresh terms list
                          console.log('Delete successful, refreshing terms list');
                          const updatedTerms = await getParliamentaryTerms();
                          console.log('Updated terms after deletion:', updatedTerms);
                          setTerms(updatedTerms);
                          console.log('Terms state updated');
                          alert('Term deleted successfully.');
                        } else {
                          console.error('Delete failed:', result);
                          alert(result.message || 'Failed to delete parliamentary term');
                        }
                      } catch (error) {
                        console.error('Error deleting term:', error);
                        alert('Failed to delete parliamentary term. Please try again.');
                      }
                    } else {
                      console.log('User cancelled deletion');
                    }
                  }}
                  className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Create Term Form */}
        {showCreateTerm && (
          <div className="mt-6 p-4 bg-gray-50 border rounded">
            <h3 className="font-semibold mb-4">Create New Parliamentary Term</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Term Name</label>
                <input
                  type="text"
                  value={newTerm.name}
                  onChange={(e) => setNewTerm({...newTerm, name: e.target.value})}
                  placeholder="e.g., Term 88"
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Start Date</label>
                <input
                  type="date"
                  value={newTerm.startDate}
                  onChange={(e) => setNewTerm({...newTerm, startDate: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Date</label>
                <input
                  type="date"
                  value={newTerm.endDate}
                  onChange={(e) => setNewTerm({...newTerm, endDate: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
            <div className="flex space-x-2 mt-4">
              <button
                onClick={handleCreateTerm}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Create Term
              </button>
              <button
                onClick={() => {
                  setShowCreateTerm(false);
                  setNewTerm({ name: '', startDate: '', endDate: '' });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Edit Term Form */}
        {showEditTerm && editingTerm && (
          <div className="mt-6 p-4 bg-gray-50 border rounded">
            <h3 className="font-semibold mb-4">Edit Parliamentary Term</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Term Name</label>
                <input
                  type="text"
                  value={editingTerm.name}
                  onChange={(e) => setEditingTerm({...editingTerm, name: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Start Date</label>
                <input
                  type="date"
                  value={editingTerm.startDate}
                  onChange={(e) => setEditingTerm({...editingTerm, startDate: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Date</label>
                <input
                  type="date"
                  value={editingTerm.endDate}
                  onChange={(e) => setEditingTerm({...editingTerm, endDate: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
            <div className="flex space-x-2 mt-4">
              <button
                onClick={handleEditTerm}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setShowEditTerm(false);
                  setEditingTerm(null);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Petition Assignment Section */}
      <div className="bg-white rounded-lg shadow-lg p-6" data-term-petitions>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Assign Petitions to Terms</h2>
          <button
            onClick={() => setShowAssignTerm(!showAssignTerm)}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
          >
            {showAssignTerm ? 'Hide Assignment' : 'Assign Petitions'}
          </button>
        </div>

        {showAssignTerm && (
          <div className="mb-6 p-4 bg-purple-50 border rounded">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Select Term for Assignment</label>
              <select
                value={selectedTermForAssignment}
                onChange={(e) => setSelectedTermForAssignment(e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="">Select a parliamentary term</option>
                {terms.map(term => (
                  <option key={term.id} value={term.id}>
                    {formatTermDisplay(term)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">
                Selected petitions: {selectedPetitions.length}
              </p>
              <div className="space-x-2">
                <button
                  onClick={() => setSelectedPetitions(allPetitions.map(p => p.id))}
                  className="text-sm bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
                >
                  Select All
                </button>
                <button
                  onClick={() => setSelectedPetitions([])}
                  className="text-sm bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
                >
                  Clear Selection
                </button>
                <button
                  onClick={handleAssignPetitionsToTerm}
                  disabled={!selectedTermForAssignment || selectedPetitions.length === 0}
                  className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700 disabled:bg-gray-300"
                >
                  Assign to Term
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {allPetitions.map(petition => {
            const petitionTerm = terms.find(term => String(term.id) === String(petition.termId));
            return (
              <div
                key={petition.id}
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedPetitions.includes(petition.id) 
                    ? 'bg-purple-100 border-purple-500' 
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => showAssignTerm && togglePetitionSelection(petition.id)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-blue-600">{petition.title}</h3>
                    <p className="text-gray-600 text-sm mt-1">{petition.description.substring(0, 150)}...</p>
                    <div className="flex items-center mt-2 text-sm text-gray-500">
                      <span>Signatures: {typeof petition.signatures === 'object' ? (petition.signatures.count || JSON.stringify(petition.signatures)) : petition.signatures}</span>
                      <span className="mx-2">•</span>
                      <span>Created by: {typeof petition.creator === 'object' ? petition.creator.username || petition.creator.displayName || 'Unknown User' : petition.creator}</span>
                      <span className="mx-2">•</span>
                      <span>Tags: {petition.tags ? (Array.isArray(petition.tags) ? petition.tags.map(tag => typeof tag === 'object' ? tag.name || JSON.stringify(tag) : tag).join(', ') : petition.tags) : 'No tags'}</span>
                    </div>
                  </div>
                  <div className="ml-4 text-right">
                    {showAssignTerm && (
                      <input
                        type="checkbox"
                        checked={selectedPetitions.includes(petition.id)}
                        onChange={() => togglePetitionSelection(petition.id)}
                        className="w-4 h-4 text-purple-600"
                      />
                    )}
                    <div className="mt-2 text-sm">
                      {petitionTerm ? (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {petitionTerm.name}
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          No term assigned
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {allPetitions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No petitions found. Create some petitions first to assign them to terms.</p>
          </div>
        )}
      </div>

      {/* Dangerous Actions Section */}
      <div className="bg-red-50 border border-red-200 rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-red-900 mb-4">⚠️ Dangerous Actions</h2>
        
        {/* Maintenance Mode */}
        <div className="bg-white border border-red-300 rounded-lg p-4 mb-4">
          <h3 className="font-semibold text-red-800 mb-2">Maintenance Mode</h3>
          <p className="text-gray-700 text-sm mb-4">
            {isMaintenanceMode
              ? 'The site is currently in maintenance mode. Only system administrators can access it.'
              : 'Put the site in maintenance mode. Only system administrators will be able to access the site.'
            }
          </p>
          <button
            onClick={handleMaintenanceMode}
            className={`px-4 py-2 rounded font-medium ${
              isMaintenanceMode
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-orange-600 text-white hover:bg-orange-700'
            }`}
          >
            {isMaintenanceMode
              ? '✅ Disable Maintenance Mode'
              : '🔧 Enable Maintenance Mode'
            }
          </button>
        </div>

        
        {/* Ban Management System */}
        <div className="bg-white border border-red-300 rounded-lg p-4 mb-4">
          <h3 className="font-semibold text-red-800 mb-2">🚫 Ban Management</h3>
          <p className="text-gray-700 text-sm mb-4">
            Manage banned users who are prohibited from creating petitions. Users are loaded from Google Sheets.
          </p>
          
          {/* Ban Stats */}
          <div className="bg-gray-50 p-3 rounded mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Total Banned Users:</span>
                <span className="ml-2 text-red-600">{banStats.total_banned_users || 0}</span>
              </div>
              <div>
                <span className="font-medium">System Status:</span>
                <span className={`ml-2 ${banStats.service_available ? 'text-green-600' : 'text-orange-600'}`}>
                  {banStats.service_available ? '✅ Active' : '⚠️ Unavailable'}
                </span>
              </div>
            </div>
          </div>

          {/* Ban Actions */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={loadBanStats}
              disabled={loading}
              className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm"
            >
              📊 Refresh Stats
            </button>
          </div>
        </div>

        {/* Petition Status Management */}
        <div className="bg-white border border-blue-300 rounded-lg p-4">
          <h3 className="font-semibold text-blue-800 mb-2">📝 Petition Status Management</h3>
          <p className="text-gray-700 text-sm mb-4">
            Manually update the status of any petition in the system.
          </p>

          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setShowStatusPanel(!showStatusPanel)}
              className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm"
            >
              {showStatusPanel ? '❌ Hide Panel' : '⚙️ Manage Status'}
            </button>
          </div>

          {showStatusPanel && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-gray-900 mb-3">Update Petition Status</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Petition Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Petition
                  </label>
                  <select
                    value={selectedPetitionId}
                    onChange={(e) => setSelectedPetitionId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select a petition --</option>
                    {allPetitions.map((petition) => (
                      <option key={petition.id} value={petition.id}>
                        {petition.title.substring(0, 50)}... ({petition.signatures} sigs)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Status
                  </label>
                  <select
                    value={newPetitionStatus}
                    onChange={(e) => setNewPetitionStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select status --</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                    <option value="awaiting_government_response">Awaiting Government Response</option>
                    <option value="government_response">Government Response</option>
                    <option value="awaiting_debate_in_parliament">Awaiting Debate in Parliament</option>
                    <option value="debated_in_parliament">Debated in Parliament</option>
                    <option value="not_debated_in_parliament">Not Debated in Parliament</option>
                  </select>
                </div>

                {/* Update Button */}
                <div className="flex items-end">
                  <button
                    onClick={handleUpdatePetitionStatus}
                    disabled={statusUpdateLoading || !selectedPetitionId || !newPetitionStatus}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {statusUpdateLoading ? '⏳ Updating...' : '✅ Update Status'}
                  </button>
                </div>
              </div>

              {/* Current Petition Info */}
              {selectedPetitionId && (
                <div className="mt-4 p-3 bg-white border border-gray-200 rounded">
                  {(() => {
                    const petition = allPetitions.find(p => p.id === selectedPetitionId);
                    return petition ? (
                      <div>
                        <h5 className="font-medium text-gray-900 mb-2">Current Petition Details:</h5>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div><strong>Title:</strong> {petition.title}</div>
                          <div><strong>Current Status:</strong> 
                            <span className="ml-1 px-2 py-1 bg-gray-100 rounded text-xs">
                              {petition.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </div>
                          <div><strong>Signatures:</strong> {petition.signatures}</div>
                          <div><strong>Creator:</strong> {petition.creator}</div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Delete All Petitions */}
        <div className="bg-white border border-red-300 rounded-lg p-4">
          <h3 className="font-semibold text-red-800 mb-2">Delete All Petitions</h3>
          <p className="text-gray-700 text-sm mb-4">
            This will permanently delete ALL user-created petitions from the system. This action cannot be undone.
            Mock petitions will remain for demonstration purposes.
          </p>
          <button
            onClick={handleDeleteAllPetitions}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-medium"
          >
            🗑️ Delete All Petitions
          </button>
        </div>
      </div>
    </div>
  );
};
